import type { StateCreator } from 'zustand';
import type { Attachment } from '@my-fullstack-app/itinerary-types';
import type { ItineraryState } from '../itinerary-store';

/**
 * Local cache of `Trip.attachments` (ADR-0060). Distinct from
 * references-slice in two key ways:
 *
 *   1. The server attachments router is the canonical writer — we
 *      never push attachment edits via sync-manager. The slice is
 *      pure cache; nothing here calls `markTripDirty`.
 *   2. Mutations here run synchronously in response to server
 *      confirmations issued by `useAttachmentMutations`. Optimistic
 *      add happens via `upsertAttachment` BEFORE the upload PUT;
 *      failure rollback is `removeAttachment` with the same id.
 */

export interface AttachmentsSlice {
  /** Insert or replace an attachment by id. Used for both
   *  optimistic insertion (state: 'pending' immediately after
   *  presign) and server-confirmed updates (state: 'uploaded' after
   *  confirm, label / attachedTo edits). */
  upsertAttachment: (tripId: string, attachment: Attachment) => void;
  /** Drop an attachment by id. Used both for user-initiated removal
   *  and for optimistic-rollback when an upload fails. */
  removeAttachment: (tripId: string, attachmentId: string) => void;
}

export const createAttachmentsSlice: StateCreator<ItineraryState, [], [], AttachmentsSlice> = (
  set
) => ({
  upsertAttachment: (tripId, attachment) => {
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        const existing = t.attachments ?? [];
        const idx = existing.findIndex((a) => a.id === attachment.id);
        const next =
          idx < 0
            ? [...existing, attachment]
            : existing.map((a, i) => (i === idx ? attachment : a));
        return { ...t, attachments: next };
      }),
    }));
  },

  removeAttachment: (tripId, attachmentId) => {
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return {
          ...t,
          attachments: (t.attachments ?? []).filter((a) => a.id !== attachmentId),
        };
      }),
    }));
  },
});
